import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Phone, Mail, DollarSign, Calendar, MessageSquare, Send, Sparkles } from 'lucide-react';

const STAGE_COLORS = {
  NEW_LEAD: 'bg-blue-100 text-blue-700',
  CONTACTED: 'bg-sky-100 text-sky-700',
  CONSULT_BOOKED: 'bg-violet-100 text-violet-700',
  NO_SHOW: 'bg-red-100 text-red-700',
  TREATMENT_PRESENTED: 'bg-amber-100 text-amber-700',
  UNSOLD_TREATMENT: 'bg-orange-100 text-orange-700',
  WON: 'bg-emerald-100 text-emerald-700',
  LOST: 'bg-slate-100 text-slate-600',
  REACTIVATED: 'bg-teal-100 text-teal-700',
};

export default function LeadDetail() {
  const { id } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msgBody, setMsgBody] = useState('');
  const [sending, setSending] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const { data } = await api().get(`/api/leads/${id}`);
        setLead(data);
      } catch { navigate('/leads'); }
      setLoading(false);
    }
    load();
  }, [id, api, navigate]);

  const sendMessage = async () => {
    if (!msgBody.trim()) return;
    setSending(true);
    try {
      await api().post('/api/messages/sms', { lead_id: id, body: msgBody, channel: 'SMS' });
      setMsgBody('');
      const { data } = await api().get(`/api/leads/${id}`);
      setLead(data);
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const generateAI = async () => {
    setAiLoading(true);
    try {
      const { data } = await api().post('/api/ai/generate-followup', {
        lead_id: id,
        treatment_type: lead?.treatment_interest,
        channel: 'SMS',
      });
      setMsgBody(data.message);
    } catch (e) { console.error(e); }
    setAiLoading(false);
  };

  const updateStage = async (stage) => {
    try {
      await api().patch(`/api/leads/${id}/pipeline`, { pipeline_stage: stage });
      const { data } = await api().get(`/api/leads/${id}`);
      setLead(data);
    } catch (e) { console.error(e); }
  };

  if (loading || !lead) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="lead-detail-page" className="max-w-5xl mx-auto">
      <button onClick={() => navigate('/leads')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-4">
        <ArrowLeft size={16} /> Back to Pipeline
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Lead Info */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-slate-900">{lead.first_name} {lead.last_name}</h2>
            <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full mt-2 ${STAGE_COLORS[lead.pipeline_stage] || 'bg-slate-100 text-slate-600'}`}>
              {lead.pipeline_stage?.replace(/_/g, ' ')}
            </span>

            <div className="mt-5 space-y-3">
              {lead.phone && <div className="flex items-center gap-2 text-sm text-slate-600"><Phone size={14} className="text-slate-400" /> {lead.phone}</div>}
              {lead.email && <div className="flex items-center gap-2 text-sm text-slate-600"><Mail size={14} className="text-slate-400" /> {lead.email}</div>}
              {lead.treatment_interest && <div className="flex items-center gap-2 text-sm text-slate-600"><Calendar size={14} className="text-slate-400" /> {lead.treatment_interest}</div>}
              {lead.estimated_value && <div className="flex items-center gap-2 text-sm text-slate-600"><DollarSign size={14} className="text-slate-400" /> ${lead.estimated_value.toLocaleString()}</div>}
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-xs text-slate-500">Source: <span className="font-medium text-slate-700">{lead.source}</span></p>
              {lead.created_at && <p className="text-xs text-slate-400 mt-1">Created: {new Date(lead.created_at).toLocaleDateString()}</p>}
            </div>
          </div>

          {/* Stage Controls */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Move to Stage</h3>
            <div className="flex flex-wrap gap-1.5">
              {Object.keys(STAGE_COLORS).map(stage => (
                <button
                  key={stage}
                  data-testid={`stage-btn-${stage}`}
                  onClick={() => updateStage(stage)}
                  disabled={lead.pipeline_stage === stage}
                  className={`text-[10px] font-semibold px-2 py-1 rounded-full transition-all disabled:opacity-30 ${STAGE_COLORS[stage]}`}
                >
                  {stage.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Appointments */}
          {lead.appointments?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Appointments</h3>
              <div className="space-y-2">
                {lead.appointments.map(a => (
                  <div key={a.id} className="text-sm p-2 bg-slate-50 rounded-lg">
                    <p className="font-medium text-slate-700">{new Date(a.date_time).toLocaleString()}</p>
                    <p className="text-xs text-slate-500">{a.status} {a.provider && `— ${a.provider}`}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: Messages */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-slate-200 flex flex-col h-[600px]">
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <MessageSquare size={16} /> Conversation
              </h3>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {(!lead.messages || lead.messages.length === 0) ? (
                <div className="flex items-center justify-center h-full text-sm text-slate-400">
                  No messages yet. Send the first message below.
                </div>
              ) : (
                lead.messages.map(msg => (
                  <div key={msg.id} className={`flex ${msg.direction === 'OUTBOUND' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm ${
                      msg.direction === 'OUTBOUND'
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-100 text-slate-900'
                    }`}>
                      <p>{msg.body}</p>
                      <p className={`text-[10px] mt-1 ${msg.direction === 'OUTBOUND' ? 'text-slate-400' : 'text-slate-500'}`}>
                        {msg.channel} — {new Date(msg.sent_at).toLocaleTimeString()}
                        {msg.ai_generated && ' (AI)'}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-4 border-t border-slate-200">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <textarea
                    data-testid="lead-message-input"
                    value={msgBody}
                    onChange={e => setMsgBody(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 resize-none"
                    placeholder="Type a message..."
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <button
                    data-testid="lead-ai-generate"
                    onClick={generateAI}
                    disabled={aiLoading}
                    className="p-2 text-violet-600 bg-violet-50 rounded-lg hover:bg-violet-100 transition-colors disabled:opacity-50"
                    title="Generate AI message"
                  >
                    <Sparkles size={16} />
                  </button>
                  <button
                    data-testid="lead-send-message"
                    onClick={sendMessage}
                    disabled={sending || !msgBody.trim()}
                    className="p-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50"
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
